import React, { useState, useEffect } from "react";

// ESRI
import GraphicsLayer from "@arcgis/core/layers/GraphicsLayer.js";
import * as geoprocessor from "@arcgis/core/rest/geoprocessor.js";
import IdentityManager from "@arcgis/core/identity/IdentityManager";

// lIbraries
import { Button, Icon, Dropdown, Popup } from "semantic-ui-react";
import axios from "axios";

// Custom
import DrawHandler from "./DrawHandler";

const PrintServiceWidget = ({ url, view, localConfig, setLocalConfig }) => {
  const [status, setStatus] = useState(false);
  const [job, setJob] = useState(null);
  const [open, setOpen] = useState(false);
  const [layer, setLayer] = useState(null);
  const [fileUrl, setFileUrl] = useState(null);
  const [drawHandler, setDrawHandler] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);

  useEffect(() => {
    // const print_layer = new GraphicsLayer({ id: "PrintServiceLayer" });
    // view.map.layers.add(print_layer);
    // setLayer(print_layer);

    // const draw_handler = new DrawHandler({
    //   view: view,
    //   layer: print_layer,
    // });

    // draw_handler.on("completed", (geometry) => {
    //   submitJob("dmcgisPortrait");
    //   setTooltipMessage(null);
    //   setTooltipVisible(false);

    //   setTimeout(() => {
    //     print_layer.graphics.removeAll();
    //     activateTool({ tool: "Identify" });
    //   }, 500);
    // });

    // setDrawHandler(draw_handler);

    if (localConfig.print_request) {
      checkPrintStatus(localConfig.print_request);
    }
  }, []);

  // --- HELPER FUNCTIONS ---

  const getBasemapConfig = (map) => {
    // Find any layer that has the "basemap" property
    const basemapLayer = map.layers.find((l) => l.basemap);

    // Filter out all layers that have the "static" property
    const staticLayers = map.layers.filter((l) => l.static);

    // console.log({ staticLayers });

    // Initialize an array to hold all the titles
    const allTitles = [];

    // Extract titles from the basemap layer's allSublayers if it exists
    if (basemapLayer && basemapLayer.allSublayers) {
      const basemapTitles = basemapLayer.allSublayers.map((s) => s.title);
      allTitles.push(...basemapTitles);
    }

    // Extract titles from all static layers' allSublayers
    staticLayers.forEach((layer) => {
      if (layer.allSublayers) {
        const staticTitles = layer.allSublayers.map((s) => s.title);
        allTitles.push(...staticTitles);
      }
    });

    return allTitles; // Return the merged array of titles
  };

  const serializeServiceLayer = (layer) => {
    if (!layer.visible || !layer.url) return null;
    if (!layer.url.includes("MapServer")) return null;
  
    let serviceUrl = null;

    if (layer.url.includes("dmcgis/IdentifiableLayers/MapServer")) {
      serviceUrl = "dmcgis\\IdentifiableLayers.MapServer";
    } else {
      // skip layers not part of the internal services we care about
      return null;
    }

    // Determine sublayer indices
    let indices = [];
    if (layer.type === "map-image" && layer.sublayers) {
      indices = layer.sublayers.filter((s) => s.visible).map((s) => s.id);
    } else {
      indices = [0];
    }

    return { url: serviceUrl, indices };
  };

  const serializeGraphicsLayer = (layers) => {
    // Initialize the feature collection object
    const featureCollection = {
      layers: [],
    };

    layers.forEach((layer) => {
      // Ensure the layer is visible and has the "print" property
      if (!layer.visible || !layer.print) return;

      const graphicsArr = Array.isArray(layer.graphics)
        ? layer.graphics
        : (layer.graphics && layer.graphics.items) || [];

      if (!graphicsArr.length) return;

      // Initialize arrays for different geometry types
      const polygons = [],
        polylines = [],
        points = [],
        textSymbols = [];

      // Process each graphic in the layer
      graphicsArr.forEach((graphic) => {
        if (!graphic.geometry) return;

        if (graphic.symbol?.type === "text") {
          textSymbols.push(graphic);
        } else if (
          graphic.geometry.type === "polygon" ||
          graphic.geometry.rings
        ) {
          polygons.push(graphic);
        } else if (
          graphic.geometry.type === "polyline" ||
          graphic.geometry.paths
        ) {
          polylines.push(graphic);
        } else if (
          graphic.geometry.type === "point" ||
          graphic.geometry.x !== undefined
        ) {
          points.push(graphic);
        }
      });

      const layerGroups = [
        { type: "polygon", features: polygons },
        { type: "polyline", features: polylines },
        { type: "point", features: points },
        { type: "text", features: textSymbols },
      ];

      // Serialize each layer group (polygon, polyline, etc.)
      layerGroups.forEach((group) => {
        if (!group.features.length) return;

        const features = group.features.map((graphic) => {
          let geomJSON;
          if (group.type === "polygon") {
            geomJSON = {
              type: "polygon",
              rings: graphic.geometry.rings,
              spatialReference: graphic.geometry.spatialReference,
            };
          } else if (group.type === "polyline") {
            geomJSON = {
              type: "polyline",
              paths: graphic.geometry.paths,
              spatialReference: graphic.geometry.spatialReference,
            };
          } else {
            geomJSON = {
              type: "point",
              x: graphic.geometry.x,
              y: graphic.geometry.y,
              spatialReference: graphic.geometry.spatialReference,
            };
          }

          // Ensure the symbol is serialized correctly
          const symbol = graphic.symbol?.toJSON
            ? graphic.symbol.toJSON()
            : graphic.symbol || getDefaultSymbolForGraphic(graphic);
          return { geometry: geomJSON, symbol, aggregateGeometries: null };
        });

        let esriGeomType = "esriGeometryPoint";
        if (group.type === "polygon") esriGeomType = "esriGeometryPolygon";
        if (group.type === "polyline") esriGeomType = "esriGeometryPolyline";

        // Push the layer with serialized data into the layers array
        featureCollection.layers.push({
          layerDefinition: {
            name: `drawLayer_${group.type}`,
            geometryType: esriGeomType,
          },
          featureSet: { geometryType: esriGeomType, features },
        });
      });
    });

    // Return the final featureCollection object wrapped in the required format
    return {
      id: "drawLayer",
      title: null,
      opacity: 1,
      minScale: 0,
      maxScale: 0,
      layerType: "Feature Layer",
      featureCollection: featureCollection,
    };
  };

  function getDefaultSymbolForGraphic(graphic) {
    const geomType = graphic.geometry?.type;
    switch (geomType) {
      case "point":
        return {
          type: "esriSMS",
          style: "esriSMSCircle",
          color: [255, 0, 0, 128],
          size: 10,
          outline: { type: "esriSLS", color: [0, 0, 0, 255], width: 1 },
        };
      case "polyline":
        return { type: "esriSLS", color: [255, 0, 0, 255], width: 2 };
      case "polygon":
        return {
          type: "esriSFS",
          style: "esriSFSSolid",
          color: [255, 0, 0, 128],
          outline: { type: "esriSLS", color: [0, 0, 0, 255], width: 1 },
        };
      default:
        return {};
    }
  }

  const getDrawnGraphics = (map) => {
    // console.warn("asdfasdf", {
    //   graphicslayer: map.layers.filter((l) => l.type === "graphics"),
    //   print_layers: map.layers.filter((l) => l.type === "graphics" && l.print),
    // });

    const graphicsLayer = map.layers.filter(
      (l) => l.type === "graphics" && l.print
    );
    if (graphicsLayer.items.length === 0) return null;
    return serializeGraphicsLayer(graphicsLayer);
  };

  // --- JOB HANDLING (LOG CONFIG ONLY) ---

  const submitJob = async (template) => {
    const config = {
      layers: getBasemapConfig(view.map),
      services: view.map.layers.map(serializeServiceLayer).filter(Boolean),
      drawn_graphics: getDrawnGraphics(view.map),
      mapOptions: {
        showAttribution: false,
        extent: {
          spatialReference: view.extent.spatialReference,
          xmin: view.extent.xmin,
          ymin: view.extent.ymin,
          xmax: view.extent.xmax,
          ymax: view.extent.ymax,
        },
        scale: view.scale,
      },
      exportOptions: {
        dpi: 150,
        template: template,
      },
    };

    // console.group("GP CONFIG PREVIEW");
    // console.log(JSON.stringify(config, null, 2));
    // console.groupEnd();

    const job = await geoprocessor.submitJob(url, {
      config,
    });
    setJob(job);

    // TEMP: prevent actual submission
    setStatus("active");

    var new_config = { ...localConfig };
    new_config.print_request = job.jobId;

    setLocalConfig(new_config);

    checkPrintStatus(job.jobId);

    return;
  };

  const checkPrintStatus = async (job_id) => {
    // console.error({ job_id, url });

    let fetchReport = () => axios.get(`${url}/jobs/${job_id}?f=json`);
    let validate = (result) => {
      // console.log("validate", { result });

      // Catch if the print fails
      if (result.data.jobStatus === "esriJobFailed") {
        setErrorMessage("Print Request failed. Click to clear.");
        setStatus("error");
        return false;
      }

      // Cancel if error
      if (result.data.error) {
        setErrorMessage("Print Request no longer exists. Click to clear.");
        setStatus("error");
        return false;
      }

      if (
        ["esriJobCancelled", "esriJobCancelling"].includes(
          result.data.jobStatus
        )
      ) {
        reset();
        return false;
      }

      // Cancel if the job succeeded
      if (result.data.jobStatus === "esriJobSucceeded") {
        // setStatus("finished");
        // setFileUrl(result.data.results.value.url);
        // setJob(null);
        return false;
      }

      if (result.data.jobStatus === "esriJobExecuting") {
        setStatus("active");
      }

      // continue looping
      return true;
    };
    let response = await poll(fetchReport, validate, 5000);

    if (response.data.jobStatus === "esriJobSucceeded") {
      axios
        .get(`${url}/jobs/${job_id}/results/output?f=json`)
        .then((result) => {
          // console.log("GET FILE", { result });
          setStatus("finished");
          setFileUrl(result.data.value.url);
          setJob(null);
        });
    }

    // console.error("Response", { response });
  };

  const poll = async function (fn, fnCondition, ms) {
    let result = await fn();
    // console.log("RESULT", { result });
    while (fnCondition(result)) {
      await wait(ms);
      result = await fn();
    }
    return result;
  };

  const wait = function (ms = 1000) {
    return new Promise((resolve) => {
      setTimeout(resolve, ms);
    });
  };

  const handlePortrait = () => submitJob("Portrait");
  const handleLandscape = () => submitJob("Landscape");

  const reset = () => {
    setStatus(false);
    var new_config = { ...localConfig };
    new_config.print_request = null;
    setLocalConfig(new_config);
  };

  const checkDropdownVisibility = () => {
    const $dropdown_menu = document.getElementById("print_dropdown_menu");
    setOpen(!$dropdown_menu.classList.contains("visible"));
  };

  // console.log("print widget", { url, fileUrl });

  // --- UI STATE ---
  if (status === "error") {
    return (
      <Popup
        trigger={
          <Button
            icon
            negative
            style={{ position: "sticky", marginBottom: "10px" }}
            onClick={() => reset()}
          >
            <Icon name={"exclamation"} />
          </Button>
        }
        inverted
        size="tiny"
        content={errorMessage || "An Error has occurred. Print Task Failed."}
        position="right center"
      />
    );
  }

  if (status === "active") {
    return (
      <Popup
        trigger={
          <Button
            icon
            secondary
            style={{ position: "sticky", marginBottom: "10px" }}
          >
            <Icon name={"notched circle"} loading style={{ height: "1em" }} />
          </Button>
        }
        inverted
        size="tiny"
        content="Building Print Config..."
        position="right center"
      />
    );
  }

  if (status === "finished") {
    return (
      <Popup
        trigger={
          <Button
            as="a"
            target="_blank"
            href={fileUrl}
            icon
            positive
            style={{ position: "sticky", marginBottom: "10px" }}
            onClick={() => {
              setStatus(false);
              reset();
            }}
          >
            <Icon name={"checkmark"} />
          </Button>
        }
        className={`transition fade in`}
        inverted
        size="tiny"
        content="Print Request Completed"
        position="right center"
      />
    );
  }

  return (
    <div style={{ position: "relative" }}>
      <Popup
        content="Print"
        position="right center"
        open={open}
        inverted
        size="tiny"
        trigger={
          <Dropdown
            icon="print"
            pointing="left"
            className="ui icon button no-margin no-focus"
            onMouseEnter={() => checkDropdownVisibility()}
            onMouseLeave={() => setOpen(false)}
            onClick={() => setOpen(false)}
          >
            <Dropdown.Menu id="print_dropdown_menu">
              <Dropdown.Header content="Print Map" />
              <Dropdown.Divider />
              <Dropdown.Item onClick={handlePortrait}>
                <Icon className="expand arrows alternate" /> Portrait
              </Dropdown.Item>
              <Dropdown.Item onClick={handleLandscape}>
                <Icon className="expand arrows alternate" /> Landscape
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        }
      />
    </div>
  );
};

export default PrintServiceWidget;
